import { Component, computed, ElementRef, Signal, ViewChild } from '@angular/core'
import { Router } from '@angular/router'
import {
    API_ERROR_CODE_FEATURE_NOT_AVAILABLE,
    BookSessionPreferences,
    ConfirmedSessionsResponse,
    SESSION_DURATION_25_MINS,
    SESSION_DURATION_50_MINS,
    User,
} from '@app/core/_models'
import {
    BookSessionInfo,
    BookSessionsService,
    CancelSessionService,
    ConfirmedSessionsService,
    UtilsService,
} from '@app/core/_services'
import {
    AnalyticsService,
    BookingSource,
    SessionTileSource,
} from '@app/core/_services/analytics.service'
import { DeviceDetectionService } from '@app/core/_services/device-detection.service'
import { DialogService } from '@app/core/_services/dialog.service'
import { ErrorHandlerService } from '@app/core/_services/error-handler.service'
import { LocalStorageAccessService } from '@app/core/_services/local-storage-access.service'
import { PlayToneService } from '@app/core/_services/play-tone.service'
import { SessionSettingsService } from '@app/core/_services/session-settings.service'
import { SESSION_STATE_SEARCHING } from '@app/core/_services/session-state.service'
import { UserService } from '@app/core/_services/user.service'
import {
    faCircleChevronUp,
    faCircleChevronDown,
    faArrowRight,
    faExternalLink,
} from '@fortawesome/pro-solid-svg-icons'
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'

import { finalize, firstValueFrom, forkJoin, Subject, take, takeUntil } from 'rxjs'
import { FeatureReleaseModalComponent } from '@app/shared-dialogs/components/feature-release-modal/feature-release-modal.component'
import { UserDateFormattingService } from '@app/core/_services/user-date-formatting.service'

type ActiveSessionState = 'none' | 'searching' | 'matched'

const USER_PROP_HAS_TRIED_NOW_SESSIONS = 'hasTriedNowSessions'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'

@Component({
    selector: 'fm-now-sessions-sidebar',
    templateUrl: './now-sessions-sidebar.component.html',
    styleUrl: './now-sessions-sidebar.component.scss',
    imports: [FontAwesomeModule],
})
export class NowSessionsSidebarComponent {
    private ngUnsubscribe: Subject<any> = new Subject<any>()

    faInfoCircle = faInfoCircle
    faCircleChevronUp = faCircleChevronUp
    faCircleChevronDown = faCircleChevronDown
    faArrowRight = faArrowRight
    faExternalLink = faExternalLink

    @ViewChild('startSessionButton') startSessionButton: ElementRef

    private user: User

    public openPanel: boolean = false
    public firstTimeViewing: boolean = false

    public bookingSession: boolean = false
    public activeSessionState: ActiveSessionState = 'none'
    public activeSession: ConfirmedSessionsResponse | null = null

    public useNewLayout = this.localStorageAccessService.getItem('newLayout')

    public sessionLengthStr: Signal<string> = computed(() => {
        // hardcodeing session duration to 50 minutes until we decide to allow now sessions of other lengths
        //const sessionDuration = this.sessionSettingsService.sessionDurationSetting()
        const sessionDuration = SESSION_DURATION_50_MINS
        if (sessionDuration === SESSION_DURATION_25_MINS) {
            return '25'
        } else if (sessionDuration === SESSION_DURATION_50_MINS) {
            return '50'
        } else {
            return '75'
        }
    })

    public buttonText = computed(() => {
        return `Start ${this.sessionLengthStr()} min session`
    })

    constructor(
        private sessionSettingsService: SessionSettingsService,
        private localStorageAccessService: LocalStorageAccessService,
        private bookSessionsService: BookSessionsService,
        private cancelSessionService: CancelSessionService,
        private userService: UserService,
        private dialogService: DialogService,
        private analyticsService: AnalyticsService,
        private playToneService: PlayToneService,
        private deviceDetectionService: DeviceDetectionService,
        private router: Router,
        private errorHandlerService: ErrorHandlerService,
        private confirmedSessionsService: ConfirmedSessionsService,
        private utilsService: UtilsService,
        private dateFormat: UserDateFormattingService,
    ) {
        this.userService.currentUser.pipe(takeUntil(this.ngUnsubscribe)).subscribe((user) => {
            this.user = user
        })

        // search for
        // - upcoming session in searching state
        // - currently ongoing session (or now session with a meeting room that has not ended)
        this.confirmedSessionsService.confirmedSessionsSubject
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((confirmedSessions) => {
                this.activeSession = null
                this.activeSessionState = 'none'
                for (let i = 0; i < confirmedSessions.length(); i++) {
                    const session = confirmedSessions.getByIndex(i)

                    // searching sessions
                    if (session.state === SESSION_STATE_SEARCHING) {
                        this.activeSession = session
                        this.activeSessionState = 'searching'
                        break
                    } else if (
                        session.sessionType === 'now' &&
                        !this.utilsService.hasSessionEnded(
                            session.sessionTime,
                            session.duration,
                            this.utilsService.getCurrentTimestamp(),
                        ) &&
                        session.meetingRoom
                    ) {
                        // ongoing matched now session
                        this.activeSession = session
                        this.activeSessionState = 'matched'
                    } else if (
                        session.sessionType === 'standard' &&
                        session.meetingRoom &&
                        this.utilsService.ongoingSession(
                            session.sessionTime,
                            session.duration,
                            this.utilsService.getCurrentTimestamp(),
                        )
                    ) {
                        // ongoing matched regular session
                        this.activeSession = session
                        this.activeSessionState = 'matched'
                    }
                }
            })

        this.bookSessionsService.bookNowSessionClickedSubject
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
                this.onClickStartSessionButton()
            })
    }

    ngOnInit() {
        this.openPanel =
            this.localStorageAccessService.getItem('nowSessionsSidebar:openPanel') === 'true'
        this.firstTimeViewing =
            this.localStorageAccessService.getItem('nowSessionsSidebar:hasViewed') !== 'true'
        if (this.firstTimeViewing) {
            this.localStorageAccessService.setItem('nowSessionsSidebar:hasViewed', 'true')
        }
    }

    togglePanel() {
        this.openPanel = !this.openPanel
        this.localStorageAccessService.setItem(
            'nowSessionsSidebar:openPanel',
            this.openPanel.toString(),
        )
    }

    async onClickStartSessionButton(): Promise<void> {
        this.analyticsService.logClickedEvent(this.buttonText(), 'Now Sessions Sidebar')

        this.playToneService.playEndSessionToneMuted()

        if (!this.deviceDetectionService.isSupportedBrowserDaily()) {
            this.dialogService.openSafariWarning()
            return
        }

        // display modal if first time using now sessions
        if (!this.user.properties[USER_PROP_HAS_TRIED_NOW_SESSIONS]) {
            const resp = await firstValueFrom(
                this.dialogService.openNowSessionsFirstTimeUseModal().afterClosed(),
            )

            if (!resp) {
                return
            }

            this.user.properties[USER_PROP_HAS_TRIED_NOW_SESSIONS] = true
            this.userService.update(this.user).subscribe()
        }

        this.bookingSession = true

        try {
            await this.bookAndJoinSession()
        } finally {
            this.bookingSession = false
        }
    }

    private joinSession(sessionTime: number): void {
        // now that the session is in the seaching state, join it
        this.playToneService.playEndSessionToneMuted()

        const routerLink = `/session/${sessionTime / 1000}`
        const routerState = { data: { rematchAtStart: true } }
        this.router.navigate([routerLink], { state: routerState })
    }

    private async bookAndJoinSession(): Promise<void> {
        // hardcoding session duration to 50 minutes until we decide to allow now sessions of other lengths
        //const sessionDuration = this.sessionSettingsService.sessionDurationSetting()
        const sessionDuration = SESSION_DURATION_50_MINS
        const favorites = this.sessionSettingsService.getSessionSettingFavoritesPreference()
        const quietMode = this.sessionSettingsService.quietModeEnabled()
        const activityType = this.sessionSettingsService.getSessionSettingActivityType()

        const preferences: BookSessionPreferences = {
            favorites: { value: favorites },
            quietMode: { value: quietMode },
        }

        let bookSessionInfo: BookSessionInfo = {
            sessionTime: -1,
            title: null,
            duration: sessionDuration,
            preferences,
            activityType,
            userAvailabilityCode: null,
            userId: null,
        }

        try {
            const response = await firstValueFrom(
                this.bookSessionsService.bookSessions(
                    [bookSessionInfo],
                    this.user.timeZone,
                    BookingSource.NowSessionsSidebar,
                ),
            )

            let parsedRes = this.bookSessionsService.parseBookResponse(response)

            if (parsedRes['numSuccess'] === 1) {
                // success
                this.joinSession(response[0].sessionTime)
                return
            } else if (parsedRes['planLimitReached'] === true) {
                this.dialogService.openPlanLimitReachedDialog()
                return
            } else if (parsedRes['sessionConflict']) {
                const conflictTimes = parsedRes['conflicts'].map((conflict) => conflict.sessionTime)
                this.dialogService
                    .openSessionConflictWithCancel(conflictTimes, this.user.timeZone)
                    .afterClosed()
                    .pipe(take(1))
                    .subscribe((actionResult) => {
                        if (actionResult.action === 'cancelAndBook') {
                            this.bookingSession = true
                            const cancelRequests = parsedRes['conflicts'].map((conflict) => {
                                return this.cancelSessionService.cancelSession(
                                    conflict.externalId,
                                    SessionTileSource.NowSessionsSidebar,
                                )
                            })

                            forkJoin(cancelRequests)
                                .pipe(finalize(() => (this.bookingSession = false)))
                                .subscribe({
                                    next: (v) => this.bookAndJoinSession(),
                                    error: (e) => {
                                        this.errorHandlerService.handleErrorResponse(e)
                                    },
                                })
                        }
                    })
            } else if (response[0]?.error?.error_code === API_ERROR_CODE_FEATURE_NOT_AVAILABLE) {
                this.dialogService.openFeatureNotAvailableModal()
            }
        } catch (error) {
            this.errorHandlerService.handleErrorResponse(error)
        }
    }

    helpIconClicked(event) {
        event.stopPropagation()
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
